import { Badge } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import { Message } from "design-system/organisms/chat/ChatMessage"
import { NoteItem } from "design-system/organisms/chat/MessageList"
import { cn } from "design-system/utils"
import { capitalize, DEFAULT_GOAL_NPS, isFilled, isNil } from "lopermut"
import { CircleCheckBig, Dot, Search } from "lucide-react"
import { useConversationGoalsQuery } from "modules/campaigns/hooks/sections/goals/useConversationGoalsQuery"
import { useConversationResponsesQuery } from "modules/conversations/hooks/useConversationResponsesQuery"
import { scrollToNote } from "modules/notes/lib/scrollToNote"
import { Loading } from "modules/shared/components/Loading"
import { NPSBadge } from "modules/shared/components/Miscelanea/NPSBadge"
import { SentimentBadge } from "modules/shared/components/Miscelanea/SentimentBadge"
import { SentimentIcon } from "modules/shared/components/Miscelanea/SentimentIcon"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { ConversationFlagResponseDataSchema, CustomerResponseDataSchema } from "schema"
import z from "zod"
import { ConversationFlags } from "./ConversationFlags"
import { MessageCapabilityExecutions } from "./MessageCapabilityExecutions"
import { MessageKnowledgeBaseItems } from "./MessageKnowledgeBaseItems"

function renderNoteText(text: string, mentions: NoteItem["mentions"] = []) {
  const parts = text.split(/(@\{#\d+\})/g)
  return parts.map((part, i) => {
    const match = part.match(/^@\{#(\d+)\}$/)
    if (match) {
      const mention = mentions.find((m) => m.id === match[1])
      if (mention) {
        return (
          <span key={i} className="font-medium !inline">
            {"@" + mention.name}
          </span>
        )
      }
    }
    return part
  })
}

type Props = {
  conversationId: string
  overallSentiment?: number | null
  selectedMessage: Message | null
  customer?: z.infer<typeof CustomerResponseDataSchema>
  noteItems?: NoteItem[]
  selectedMessageFlags?: z.infer<typeof ConversationFlagResponseDataSchema>[]
  conversationFlags: z.infer<typeof ConversationFlagResponseDataSchema>[]
  showFlags: boolean
}

export const ConversationInsights = ({
  conversationId,
  overallSentiment,
  selectedMessage,
  noteItems,
  selectedMessageFlags = [],
  conversationFlags,
  showFlags,
}: Props) => {
  const { t } = useTranslation()
  const visibleFlags = selectedMessage ? selectedMessageFlags : conversationFlags

  const { data: responses, isPending } = useConversationResponsesQuery(conversationId)
  const { data: goals, isPending: isGoalsPending } = useConversationGoalsQuery({
    conversationId,
  })

  const npsResponse = useMemo(
    () => responses?.find((resp) => resp.goalName === DEFAULT_GOAL_NPS),
    [responses],
  )
  const hasNps = useMemo(() => goals?.some((goal) => goal.goal.name === DEFAULT_GOAL_NPS), [goals])

  const sortedResponses = useMemo(
    () =>
      responses
        ?.filter((resp) => resp.goalName !== DEFAULT_GOAL_NPS)
        .filter((resp, idx, arr) => arr.findIndex((x) => x.response === resp.response) === idx)
        .sort((a, b) => (b.sentiment ?? 0) - (a.sentiment ?? 0)),
    [responses],
  )

  const predefinedResponses = useMemo(
    () => sortedResponses?.filter((e) => e.goalOptionValue),
    [sortedResponses],
  )

  const openResponses = useMemo(
    () => sortedResponses?.filter((resp) => !resp.goalOptionValue && resp.sentiment !== null),
    [sortedResponses],
  )

  const noSentimentOpenResponses = useMemo(
    () => sortedResponses?.filter((resp) => !resp.goalOptionValue && resp.sentiment === null),
    [sortedResponses],
  )

  if (isPending || isGoalsPending) return <Loading />

  function renderGoals() {
    if (isGoalsPending) return <Loading />
    if (goals?.length === 0)
      return (
        <span className="text-sm text-muted-foreground">
          {t("conversations.chat.insights.noGoals")}
        </span>
      )

    return goals?.map((goal) => {
      const isGoalAnswered = isFilled(goal.achievedAt)
      return (
        <div key={`${conversationId}-${goal.goal.id}`} className="flex flex-row items-center gap-2">
          <CircleCheckBig
            className={cn("flex shrink-0", isGoalAnswered ? "text-success" : "text-neutral")}
            size={16}
          />
          <span className="flex text-sm">{goal.goal.displayName}</span>
        </div>
      )
    })
  }

  function renderResponses() {
    if (isPending) return <Loading />
    if (responses?.length === 0)
      return (
        <div className="flex flex-row gap-2 items-center text-sm">
          <SentimentIcon sentiment={2} className="text-muted-foreground/50" />
          <span className="text-sm text-muted-foreground">
            {t("conversations.chat.insights.noResponses")}
          </span>
        </div>
      )

    return (
      <>
        {predefinedResponses && predefinedResponses.length > 0 && (
          <div className="flex flex-col gap-2">
            {predefinedResponses.map((resp) => (
              <div key={resp.id} className="flex flex-row gap-2 items-center text-sm">
                <span className="text-sm font-light">{resp.goalDisplayName}</span>
                <Badge variant={"outline"}>{resp.goalOptionValue}</Badge>
              </div>
            ))}
          </div>
        )}
        {openResponses?.map((resp) => (
          <div className="flex flex-col gap-2" key={resp.id}>
            <div className="flex flex-row gap-2 items-center text-sm">
              <div>
                <SentimentIcon sentiment={resp.sentiment!} showTooltip />
              </div>
              <span>{capitalize(resp.response)}</span>
            </div>
          </div>
        ))}
        {noSentimentOpenResponses?.map((resp) => (
          <div className="flex flex-col gap-2" key={resp.id}>
            <div className="flex flex-row gap-2 items-center text-sm">
              <div>
                <Dot size={20} className="text-muted-foreground" />
              </div>
              <span>{capitalize(resp.response)}</span>
            </div>
          </div>
        ))}
      </>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col">
          <span className="text-lg">{t("conversations.chat.insights.title")}</span>
        </div>
        <div className="flex flex-col gap-1">{renderResponses()}</div>
        <div className="flex flex-row justify-between">
          <div className="flex flex-col gap-1 items-start w-full">
            <span className="text-base font-medium">
              {t("conversations.chat.insights.sentiment")}
            </span>
            {isNil(overallSentiment) && (
              <span className="text-sm text-muted-foreground">
                {t("conversations.chat.insights.waitingForResponses")}
              </span>
            )}
            {!isNil(overallSentiment) && <SentimentBadge sentiment={overallSentiment} />}
          </div>
          {hasNps && (
            <div className="flex flex-col gap-2 items-start w-full">
              <span className="text-base font-medium">{t("conversations.chat.insights.nps")}</span>
              <NPSBadge
                nps={
                  npsResponse?.goalOptionValue ? parseInt(npsResponse.goalOptionValue) : undefined
                }
              />
            </div>
          )}
        </div>
        {goals && goals.length > 0 && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <span className="text-base font-medium">
                {t("conversations.chat.insights.goals")}
              </span>
              {renderGoals()}
            </div>
          </div>
        )}
      </div>
      {showFlags && (
        <div className="flex flex-col gap-2">
          <span className="text-lg">{t("conversations.chat.flags.title")}</span>
          <ConversationFlags
            flags={visibleFlags}
            scope={selectedMessage ? "message" : "conversation"}
          />
        </div>
      )}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <span className="text-lg">{t("conversations.chat.knowledgeBaseItems.title")}</span>
          {selectedMessage && selectedMessage.role === "assistant" ? (
            <MessageKnowledgeBaseItems messageId={selectedMessage.id} />
          ) : (
            <span className="text-sm text-muted-foreground">
              {t("conversations.chat.knowledgeBaseItems.selectMessage")}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-lg">{t("conversations.chat.messageCapabilityExecutions.title")}</span>
        {selectedMessage && selectedMessage.role === "assistant" ? (
          <MessageCapabilityExecutions key={selectedMessage.id} messageId={selectedMessage.id} />
        ) : (
          <span className="text-sm text-muted-foreground">
            {t("conversations.chat.messageCapabilityExecutions.selectMessage")}
          </span>
        )}
      </div>

      {noteItems && noteItems.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-col">
            <span className="text-lg font-medium">
              {t("conversations.chat.notes.conversationNotes")}
            </span>
            <span className="text-xs text-muted-foreground">
              {t("conversations.chat.notes.conversationNotesDescription")}
            </span>
          </div>
          <div className="flex flex-col gap-3">
            {noteItems.map((note) => (
              <Button
                key={note.id}
                variant={"ghost"}
                onClick={() => scrollToNote(note.id)}
                title={note.text}
                className="group flex items-start justify-between gap-2 text-left border-l-2 rounded-none pl-2 py-1 font-normal h-auto"
              >
                <div className="text-sm italic !whitespace-pre-line !overflow-visible !text-clip !line-clamp-none w-full">
                  {renderNoteText(note.text, note.mentions)}
                </div>
                <Search
                  size={14}
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground"
                />
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
